import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Upload,
  FileText,
  Package,
  ArrowRight,
  Clock,
  Building2,
  MapPin,
  Filter,
  X,
} from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const isAdmin = user?.role === "admin";

  // Load client list once (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/clients");
        setClients(data || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isAdmin]);

  // Reload stats and batches whenever the filter changes
  useEffect(() => {
    (async () => {
      try {
        const params = isAdmin && selectedClient ? { client_id: selectedClient } : {};
        const { data } = await api.get("/batches", { params });
        setBatches(data);
      } catch (e) {
        console.error(e);
      }
      if (isAdmin) {
        try {
          const params = selectedClient ? { client_id: selectedClient } : {};
          const { data } = await api.get("/admin/stats", { params });
          setStats(data);
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [isAdmin, selectedClient]);

  const selectedClientObj = clients.find((c) => c.id === selectedClient);
  const totalDocs = batches.reduce((s, b) => s + (b.total_docs || 0), 0);
  const recent = batches.slice(0, 5);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow={isAdmin ? "Panel Administrador" : `Bienvenido, ${user?.name}`}
          title="Dashboard"
          description={
            isAdmin
              ? selectedClientObj
                ? `Vista filtrada — ${selectedClientObj.razon_social || selectedClientObj.name || selectedClientObj.email}`
                : "Visión global de la operación EXIS Pharma."
              : "Resumen de tus productos cargados y su distribución por comuna."
          }
          actions={
            <Link
              to="/upload"
              data-testid="dashboard-upload-cta"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              <Upload className="w-4 h-4" />
              Cargar XML
            </Link>
          }
        />

        {/* Admin client filter */}
        {isAdmin && (
          <div
            data-testid="admin-client-filter"
            className="mb-6 bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-3"
          >
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              <Filter className="w-3.5 h-3.5 text-[#D95A40]" />
              Filtrar por cliente
            </div>
            <select
              data-testid="admin-client-select"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="flex-1 min-w-[240px] max-w-md border border-slate-300 bg-white px-3 py-2 text-sm text-[#0B1B3D] focus:outline-none focus:border-[#D95A40]"
            >
              <option value="">— Todos los clientes —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.razon_social || c.name || c.email) +
                    (c.rut ? ` · ${c.rut}` : "")}
                </option>
              ))}
            </select>
            {selectedClient && (
              <button
                type="button"
                data-testid="admin-client-clear"
                onClick={() => setSelectedClient("")}
                className="flex items-center gap-1 px-3 py-2 border border-slate-200 hover:border-[#D95A40] hover:text-[#D95A40] text-xs font-bold uppercase tracking-[0.15em] text-slate-600 transition-colors"
              >
                <X className="w-3 h-3" />
                Limpiar
              </button>
            )}
            {selectedClientObj && (
              <Link
                to={`/admin/clientes/${selectedClientObj.id}`}
                data-testid="admin-client-open-detail"
                className="flex items-center gap-1 px-3 py-2 bg-[#0B1B3D] hover:bg-[#0f2450] text-white text-xs font-bold uppercase tracking-[0.15em] transition-colors"
              >
                Ver ficha
                <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <KpiCard
            testid="kpi-total-batches"
            label={isAdmin ? "Envíos cargados" : "Productos cargados"}
            value={isAdmin ? stats?.total_batches ?? "—" : batches.length}
            icon={<Package className="w-4 h-4" />}
          />
          <KpiCard
            testid="kpi-total-docs"
            label="Documentos procesados"
            value={isAdmin ? stats?.total_documents ?? "—" : totalDocs}
            icon={<FileText className="w-4 h-4" />}
          />
          <KpiCard
            testid="kpi-recent"
            label="Últimos 7 días"
            value={
              isAdmin
                ? stats?.recent_batches_7d ?? "—"
                : batches.filter(
                    (b) =>
                      new Date(b.created_at) >
                      new Date(Date.now() - 7 * 86400000)
                  ).length
            }
            icon={<Clock className="w-4 h-4" />}
          />
          <KpiCard
            testid="kpi-clients"
            label={
              isAdmin
                ? selectedClientObj
                  ? "Unidades del cliente"
                  : "Clientes activos"
                : "Comunas atendidas"
            }
            value={
              isAdmin
                ? selectedClientObj
                  ? batches.reduce((s, b) => s + (b.total_unidades || 0), 0) || "—"
                  : stats?.total_clients ?? "—"
                : new Set(
                    batches.map((b) => b.id_cliente).filter(Boolean)
                  ).size || "—"
            }
            icon={
              isAdmin ? (
                <Building2 className="w-4 h-4" />
              ) : (
                <MapPin className="w-4 h-4" />
              )
            }
          />
        </div>

        {/* Two column: Recent batches + Distribution */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent batches */}
          <div className="lg:col-span-2 bg-white border border-slate-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="font-display font-bold text-lg text-[#0B1B3D]">
                  Lotes recientes
                </h3>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">
                  Últimas cargas de XML
                </div>
              </div>
              <Link
                to="/batches"
                className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#D95A40] hover:underline flex items-center gap-1"
                data-testid="dashboard-see-all-batches"
              >
                Ver todos <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recent.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-500">
                  Aún no has cargado documentos.{" "}
                  <Link
                    to="/upload"
                    className="text-[#D95A40] font-semibold hover:underline"
                  >
                    Cargar ahora
                  </Link>
                </div>
              )}
              {recent.map((b) => (
                <Link
                  key={b.id}
                  to={`/batches/${b.id}`}
                  data-testid={`recent-batch-${b.id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-[#0B1B3D] truncate">
                      {b.descripcion || "Lote sin descripción"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                      <span>
                        {new Date(b.created_at).toLocaleString("es-CL")}
                      </span>
                      {isAdmin && b.user_email && (
                        <span className="text-slate-400">
                          {b.user_email}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <div className="font-display font-black text-2xl text-[#0B1B3D] tabular-nums leading-none">
                      {b.total_docs}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                      documentos
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Distribution or activity */}
          <div className="bg-white border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-display font-bold text-lg text-[#0B1B3D]">
                {isAdmin ? "Top comunas" : "Guía rápida"}
              </h3>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">
                {isAdmin ? "Distribución de documentos" : "Cómo funciona"}
              </div>
            </div>
            <div className="p-6">
              {isAdmin ? (
                <div className="space-y-3">
                  {(stats?.by_comuna || []).slice(0, 8).map((c, i) => (
                    <div
                      key={c.comuna + i}
                      data-testid={`stat-comuna-${i}`}
                      className="flex items-center justify-between gap-3"
                    >
                      <div className="text-sm text-[#0B1B3D] truncate flex-1">
                        {c.comuna}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="h-1 bg-slate-100 w-24 relative">
                          <div
                            className="h-full bg-[#D95A40]"
                            style={{
                              width: `${Math.min(
                                100,
                                (c.count /
                                  ((stats.by_comuna[0]?.count || 1))) *
                                  100
                              )}%`,
                            }}
                          />
                        </div>
                        <div className="font-display font-bold text-sm tabular-nums w-8 text-right">
                          {c.count}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!stats?.by_comuna || stats.by_comuna.length === 0) && (
                    <div className="text-sm text-slate-500 text-center py-6">
                      Sin datos aún.
                    </div>
                  )}
                </div>
              ) : (
                <ol className="space-y-4 text-sm">
                  <QuickStep
                    n="1"
                    title="Sube tus XML"
                    desc="Individuales o comprimidos en ZIP. Aceptamos facturas y guías de despacho SII."
                  />
                  <QuickStep
                    n="2"
                    title="Revisa tus productos"
                    desc="Verifica los productos extraídos y su distribución por comuna. Ajusta unidades o bultos si es necesario."
                  />
                  <QuickStep
                    n="3"
                    title="Distribución por comuna"
                    desc="El administrador consolida y descarga el archivo SAP con todas las entregas listas para el sistema logístico."
                  />
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function KpiCard({ label, value, icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-slate-200 p-5 transition-transform hover:-translate-y-[1px]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <div className="text-[#D95A40]">{icon}</div>
      </div>
      <div className="font-display font-black text-4xl text-[#0B1B3D] tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function QuickStep({ n, title, desc }) {
  return (
    <li className="flex gap-3">
      <div className="shrink-0 w-7 h-7 bg-[#0B1B3D] text-white font-display font-black text-sm flex items-center justify-center">
        {n}
      </div>
      <div>
        <div className="font-semibold text-[#0B1B3D]">{title}</div>
        <div className="text-xs text-slate-600 mt-0.5">{desc}</div>
      </div>
    </li>
  );
}
